
import { fn, INamed } from '@common/utils';
import { ReplaceObservableData, AsObservableArray } from '@common/navkendo/kutils';
import { IPageMetaData, IPageProperty, IPageQueryConstraint, NavClientService, IOperator } from '@common/navdata';
import { CreateSetting } from '@common/services/settingsservice';
import { IObservableArray } from '@common/nav';

/** Container of Search input items */
export class SearchInputs extends Array<SearchInput> {
    constructor(c?: {
        AvailableProperties?: ArrayLike<IPageProperty>,
        ShowOperators?: boolean,
        AutoSelect?: boolean;
    }) {
        super();

        // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        if (!this.RefreshAvailableProperties)
            (<any>Object).setPrototypeOf(this, SearchInputs.prototype);

        c = c || <any>{};
        this.AvailableProperties = AsObservableArray((c.AvailableProperties) || []);
        this.ShowOperators = !!c.ShowOperators;
        this.AutoSelect = !!c.AutoSelect;
    }

    /** Container of properties available for User Selection */
    public readonly AvailableProperties: IObservableArray<IPageProperty>;

    /** for a Filter Setup we might want to use Operators, (for NAV Selection not) */
    ShowOperators = false;

    /** true : New Item will be preSelected with first not yet ui Bound Property */
    AutoSelect = false;

    private Client?: NavClientService;

    /** Append new Search Input  */
    AppendSearchInput(reference?: SearchInput) {
        var idx = reference ? this.indexOf(reference) : (this.length - 1),
            newInput = new SearchInput(this);
        //  Display first not yet displayed property
        if (this.AutoSelect) {
            var firstNotShown = this.AvailableProperties.filter(p => !fn.FindFirst(this, s => s.SelectedProperty == p))[0];
            if (firstNotShown) {
                newInput.SetProperty(firstNotShown);
            }
        }
        this.splice(idx + 1, 0, newInput);
        return newInput;
    }

    /** Get or Imp Create Put Item */
    GetInput(prop: string | IPageProperty, impCreate: boolean) {
        if (!prop)
            return;
        var propName: string;
        if (!(typeof prop === "string"))
            propName = prop.Name;
        else
            propName = prop;

        var result = fn.FindFirst(this, item => item.SelectedProperty && item.SelectedProperty.Name == prop);

        if (!result && impCreate) {
            result = this.AppendSearchInput();
            result.SetProperty(prop);
            if (!result.SelectedProperty) {
                this.RemoveSearchInput(result);
                result = undefined;
            }
        }

        return result;
    }

    /** Remove Search Input */
    RemoveSearchInput(toDelete: SearchInput) {
        var idx = this.indexOf(toDelete);
        if (idx >= 0) {
            this.splice(idx, 1);
        }
    }

    /** Explicit Set search Inputs By name */
    SetSearchInputs(names: string[]) {
        if (names && names.length) {
            this.refreshSearchCount(names.length, true);
            names.forEach((propName, idx) => {
                var si = this[idx];
                if (si) {
                    si.SetProperty(propName);
                }
            });
        } else if (this.length == 0) {
            // Ensure at least on Search Input
            this.refreshSearchCount(1);
        }
    }

    /** Set/Refresh Available Poperties */
    public SetAvailableProperties(newProperties: ArrayLike<IPageProperty>) {
        var props = this.AvailableProperties;
        // Full Replace Content with new Values
        props.splice(0, props.length, ...fn.ToArray(newProperties));

        // re)Intialize Search Inputs Properties
        this.forEach((item, idx) => {
            var selProp = item.SelectedProperty,
                propName = selProp && (selProp.AliasName || selProp.Name);
            item.SetProperty(propName);
        });
    }

    /** Refresh Available Properties from given Config building commonn Properties Set from given Pages  */
    RefreshAvailableProperties(c: {
        NavClient: NavClientService,
        NavUrl: string,
        /** If multiple Pages : Common Properties joined by Alias Names will be Assigned */
        Pages: string | string[],
        /** Prop (Alias-) names to Exclude explicitly */
        Exclude?: string[],
        /** Prop (Alias-) namesto restrict to */
        Include?: string[]
    }) {

        var cPages = c.Pages,
            navClient = this.Client = c.NavClient,
            navUrl = c.NavUrl,
            cExclude = c.Exclude || [],
            cInclude = c.Include || [],
            pageNames = (Array.isArray(cPages) ? cPages : [cPages]).filter(fn.isSet),
            startedRefresh: PromiseLike<void>;

        return startedRefresh = this.currRefresh = navClient.EnsurePageMetaData(pageNames, navUrl).then((metaInfos) => {
            let mergedPropertyInfo = reduceAndAdd(pageNames,
                pageName => metaInfos[pageName] ? metaInfos[pageName].Properties.filter(item => item.Invisible !== true && (cExclude.indexOf(item.Name) < 0)) : <any>[], (p: IPageProperty) => p.AliasName || p.Name,
                false // false : build set diff, true : build set union
            ).map(item => {
                return {
                    Properties: item,
                    Name: item[0].AliasName || item[0].Name,
                    Title: item[0].Title
                };
            });

            if (cInclude.length > 0) {
                // Filter by explicit Properties
                mergedPropertyInfo = cInclude.map(pName => mergedPropertyInfo.filter(item => item.Name == pName)[0]).filter(fn.isSet);
            }
            // As requested : Sort Search Fields By Title
            mergedPropertyInfo = mergedPropertyInfo.filter(fn.isSet).sort((a, b) => a.Title < b.Title ? -1 : (a.Title > b.Title ? 1 : 0));

            var newProperties = mergedPropertyInfo.map(item => {
                var fProp = item.Properties[0];
                var result = fn.clone(fProp);
                var uniqueTitle = fn.DictToArray(fn.GroupBy(item.Properties, item => item.Title)).length == 1;
                result.Title = uniqueTitle ? fProp.Title : (fProp.AliasTitle || fProp.Title);
                return result;
            });
            // ensure no new Operation Started in the meantime
            if (this.currRefresh === startedRefresh) {
                this.SetAvailableProperties(newProperties);
            }
        });
    }
    private currRefresh: PromiseLike<void>;

    /**  Get Current State as Constraints */
    GetAsConstraints = (metaData?: IPageMetaData) => this.map(item => item.GetAsConstraint(metaData, this.Client)).filter(fn.isSet);

    /**  Get Current State as Filter Items */
    GetAsFilterItems = (metaData?: IPageMetaData) => this.map(item => item.GetAsFilterItem(metaData)).filter(fn.isSet);

    // Refresh Number of search Inputs
    refreshSearchCount(searchCount: number, fullRefresh?: boolean) {
        searchCount = Number(searchCount);
        // Max Value, Default
        if (searchCount > 100)
            searchCount = 100;
        else if (!(searchCount > 0))
            searchCount = 4;

        if (fullRefresh) {
            this.length = 0;
        }

        var diff = searchCount - this.length;
        if (diff > 0) {
            // Add
            (<any[]>Array.apply(null, new Array(diff))).forEach(item => this.AppendSearchInput(this[this.length - 1]));
        } else if (diff < 0) {
            // remove
            this.slice(searchCount + 1, this.length).forEach(item => this.RemoveSearchInput(item));
        }
    }

    // Define as Settings Storage
    Settings = () => CreateSetting(() => {
        return {
            /** Properties */
            Props: this.filter(item => !!item.SelectedProperty).map(item => {
                var prop = item.SelectedProperty;
                return {
                    /** Name of the Property */
                    Name: prop.AliasName || prop.Name,
                    Value: item.QueryString
                };
            })
        };
    }, (state) => {
        if (!state || !Array.isArray(state.Props))
            return;
        var props = state.Props.filter(item => item.Name && typeof (item.Name) == "string");

        if (props.length > 0) {
            this.refreshSearchCount(props.length, true);
            this.forEach((si, idx) => {
                var prop = props[idx],
                    propName = (prop && prop.Name) || "",
                    availProps = si.AvailableProperties;

                if (!si.SetProperty(propName)) {
                    // DIRTY : Available Properties change might be still pending ... so try again after next change
                    if (propName && availProps && availProps.one) {
                        availProps.one("change", () => si.SetProperty(propName));
                    }
                }
                si.QueryString = (prop && prop.Value) || "";
            });
        }
    })

    /** Directive to display Search Inputs Model */
    static Directive = [(): ng.IDirective => {
        return {
            controller: ["$scope", ($scope: ng.IScope & { SearchInputs: SearchInputs }) => {
                if ($scope.SearchInputs && !($scope.SearchInputs instanceof SearchInputs)) {
                    throw new Error("'extinputs' must be of type 'SearchInputs'");
                }
            }],
            scope: {
                // function to determine External Inputs Container to be Displayed (initial evaluation only)
                SearchInputs: "=extinputs",
                // function to be trigger Execution (VK_RETURN on input ctrl's)
                triggerexecute: "=",
            },
            template: require('./searchinputs.html')
        };
    }];
}

/** Model Object for a Search Input Description */
export class SearchInput {
    constructor(public SearchInputs: SearchInputs) { this.AvailableProperties = SearchInputs.AvailableProperties; }

    /** Available Properties */
    public AvailableProperties: IObservableArray<IPageProperty>;

    /** Selectable Operator Items */
    public AvailableOperators = AsObservableArray<IOperator>([]);

    /** Currently Selected Operator */
    public SelectedOperator: IOperator;

    /** Currently Selected Property */
    public SelectedProperty: IPageProperty;

    /** User provided Query String for this Element */
    public QueryString = "";

    /** Set Property from Name by searching in Available Properties  (refresh wOut Param) */
    public SetProperty(propName: string | IPageProperty, value?: string, opValue?: string): IPageProperty {
        if (propName === this.SelectedProperty && value == undefined && opValue == undefined)
            return <IPageProperty>propName;
        function IsPageProperty(item: any): item is IPageProperty { return item && item.Name; };
        if (IsPageProperty(propName)) {
            return this.SetProperty(propName.Name, value, opValue);
        }

        var prop = this.SelectedProperty;
        if (propName === undefined)
            propName = (prop && (prop.AliasName || prop.Name)) || undefined;

        var prop = this.SelectedProperty = propName && fn.FindFirst(this.AvailableProperties, p => p.AliasName == propName || p.Name == propName);
        if (prop) {
            if (value !== undefined)
                this.QueryString = value;

            if (this.SearchInputs.ShowOperators) {
                var ops: INamed<any> = SearchInput.operatorMap[prop && prop.Type] || SearchInput.operatorMap.default,
                    newOperators = fn.DictToArray(fn.Remap(ops || {}, (title: string, op: string) => { return { op: op, title: title }; }));

                ReplaceObservableData(this.AvailableOperators, newOperators, item => item.op);
                if (opValue == undefined)
                    opValue = this.SelectedOperator && this.SelectedOperator.op;

                this.SelectedOperator = fn.FindFirst(this.AvailableOperators, op => op && (op.op == opValue)) || this.AvailableOperators[0];
            }
        }
        return prop;
    }

    /** Get Input Model as Constraint for a Nav Page Query */
    public GetAsConstraint(metaData?: IPageMetaData, service?: NavClientService): IPageQueryConstraint {
        var prop = this.SelectedProperty;
        if (!prop || !prop.Name || !this.QueryString)
            return;

        var result: IPageQueryConstraint = {
            FieldName: this.resolvePropertyName(prop.Name, metaData),
            Value: this.QueryString
        };
        if (service && metaData) {
            service.PrepareConstraint(result, metaData.PageName);
        }
        var prop = metaData ? metaData.PropsByName[result.FieldName] : undefined;
        result.Value = <string>getResolvedFilterString(prop, result.Value);
        return result;

        /** Resolve User Input to Validated Query string */
        function getResolvedFilterString(prop: IPageProperty, rawInputValue: string | any[]) {
            if (!prop)
                return rawInputValue;

            // Decimal : Force Eval as Expression ('10,99' will be not recognized otherwise (but '=10,99' will)
            if (prop.Type == "Decimal")
                return "( " + rawInputValue + " )";

            if (prop.Type == "String") {
                if (Array.isArray(rawInputValue)) {
                    // Caution : 'Regular' Enum Types get remapped on server side,
                    // this is necessary as we 'misuse' EnumMappings e.g.for mapping Users to string UserCode fields
                    return rawInputValue.join(' | ');
                }
                // Strings : IgnoreCase, inside if no explicit logic from user or is '...'
                else if (fn.indexOfAny(rawInputValue, "@*<>,=|{}!") >= 0 || /^\'.*\'$/.test(rawInputValue))
                    return rawInputValue;
                else
                    return "@*" + rawInputValue + "*";
            } else {
                return rawInputValue;
            }
        }
    }

    /** Get As Filter Item */
    public GetAsFilterItem(metaData?: IPageMetaData): kendo.data.DataSourceFilterItem {
        var prop = this.SelectedProperty;
        if (!prop || !prop.Name)
            return;
        var fieldName = this.resolvePropertyName(prop.Name, metaData),
            prop = metaData ? metaData.PropsByName[fieldName] : undefined;
        return {
            field: fieldName,
            operator: (this.SelectedOperator && this.SelectedOperator.op) || undefined,
            value: this.QueryString
        };
    }

    /** Resolve Property name handling alias Names if page MetaData is given */
    private resolvePropertyName(name: string, metaData?: IPageMetaData) {
        if (!metaData || !metaData.Properties) {
            return name;
        }
        const availableProp = this.AvailableProperties.filter(item => item.Name == name || item.AliasName == name)[0];
        if (!availableProp) {
            return name;
        }
        // Precedence : match by AliasName first (if AliasName is itself a valid Name ...)
        const prop = metaData.Properties.filter(item => item.AliasName === availableProp.AliasName)[0] ||
            metaData.Properties.filter(item => item.Name === availableProp.Name)[0];
        return prop ? prop.Name : name;
    }

    private static operatorMap = {
        "default": {
            "eq": "Ist gleich"
        },
        "String": {
            "eq": "Ist gleich",
            "neq": "Ist nicht gleich",
            "contains": "Beinhaltet",
            "doesnotcontain": "Beinhaltet nicht",
            "endswith": "Endet mit",
            "startswith": "Beginnt mit"
        },

        "Decimal": {
            "eq": "Ist gleich",
            "gt": "Ist größer als",
            "gte": "Ist größer als oder gleich",
            "lt": "Ist kleiner",
            "lte": "Ist kleiner als oder gleich",
            "neq": "Ist nicht gleich"
        },
        "DateTime": {
            "eq": "Ist gleich",
            "gt": "Ist nach",
            "gte": "Ist nach oder gleich",
            "lt": "Ist vor",
            "lte": "Ist vor oder gleich",
            "neq": "Ist nicht gleich"
        }
    };
}

/** Reduce Items removing duplicates by given key, either return union or intersection */
function reduceAndAdd<T, TOut>(baseItems: T[], getter: (item: T) => TOut[], keyGetter: (item: TOut) => string, union: boolean) {
    var keys = <INamed<TOut[]>>{};
    var keyCount = <INamed<number>>{};
    var itemCount = 0;
    baseItems.forEach(baseItem => {
        if (baseItem) {
            itemCount++;
            (getter(baseItem) || []).forEach(checkItem => {
                if (checkItem) {
                    var checkKey = keyGetter(checkItem);
                    if (!keys[checkKey]) {
                        keys[checkKey] = [checkItem];
                    } else {
                        keys[checkKey].push(checkItem);
                    }

                    var currCount = keyCount[checkKey] || 0;
                    keyCount[checkKey] = currCount + 1;
                }
            });
        }
    });
    var itemFilter = (key: string) => true;
    if (!union)
        itemFilter = key => keyCount[key] == itemCount;

    return Object.getOwnPropertyNames(keys).filter(itemFilter).map(item => keys[item]);
}
